import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, ArkUnitOfWork, AuthService, UtilityService } from 'app/core/core.module';
import { Invoice, InvoiceLine, Person } from 'app/core/model/entity-model';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { InvoiceLineDialogComponent } from '../invoice-line-dialog/invoice-line-dialog.component';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';

@Component({
    selector: 'app-invoice-detail',
    templateUrl: './invoice-detail.component.html',
    styleUrls: ['./invoice-detail.component.scss'],
})
export class InvoiceDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    invoiceId: any;
    invoice: Invoice;
    invoiceLines: InvoiceLine[];

    people: Person[];
    paymentMethods = ['Cash', 'Check', 'Other'];

    readOnly = false;
    processing = false;
    showAsDialog = false;

    isToastVisible = false;
    validationGroup = 'mainValidationGroup';
    errorAction = 'saving';
    saving = false;

    taxableSubTotal: number;
    initialTotal: number;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: ArkUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        protected location: Location,
        private utility: UtilityService
    ) {}

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    async ngOnInit() {
        if (!this.invoiceId) this.invoiceId = this.route.snapshot.params['id'];
        const people = (await this.uow.people.all(null, 'family')) as Person[];
        this.people = _.orderBy(
            people.filter((person) => !person.deceasedDate),
            ['lastName', 'firstName'],
            ['asc', 'asc']
        );
        await this.getData();
    }

    async getData() {
        this.appInfo.pleaseWaitShow();
        const today = moment(new Date()).startOf('day').toDate();
        if (this.invoiceId === 'new') {
            this.invoice = this.uow.invoices.createEntity({
                invoiceDate: today,
                created: today,
                createdBy: this.auth.currentUser.userName,
                paymentMethod: 'Cash',
            }) as Invoice;
            this.invoiceLines = [];
        } else {
            this.invoice = (await this.uow.invoices.byId(this.invoiceId, 'person, invoiceLines.auctionPurchase.auctionItem')) as Invoice;
            this.invoiceLines = this.invoice.invoiceLines.filter((l) => !l.deleted).slice();
        }

        this.initialTotal = this.invoice.total;
        this.calcTotals();
        this.appInfo.pleaseWaitHide();
    }

    async personSelectionChanged(e) {
        this.appInfo.pleaseWaitShow();
        // const customer = await this.uow.customers.byId(this.invoice.customerId);
        // this.invoice.customer = customer;
        // this.invoice.customerTaxCodeId = customer?.taxCodeId;
        // this.copyBilling();
        // this.calcTotals();
        this.appInfo.pleaseWaitHide();
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    rowPrepare(e) {}
    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                template: 'toolbarText',
                location: 'before',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            }
        );
    }
    async onRowDoubleClick(e) {
        if (this.readOnly) return;
        this.openLine(e.data);
    }

    async delete() {
        const result = await confirm(`Are you sure you want to delete this invoice?`, this.appInfo.settings.church.toUpperCase());
        if (!result) return;

        this.appInfo.pleaseWaitShow();

        this.invoice.deleted = new Date();
        this.invoice.deletedBy = this.auth.currentUser.userName;
        this.invoice.invoiceLines.forEach((line) => {
            line.auctionPurchaseId = null;
            line.deleted = new Date();
            line.deletedBy = this.auth.currentUser.userName;
        });

        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
        this.router.navigate(['/invoices/overview']);
    }

    async markAsPaid() {
        if (!this.invoice.paymentMethod) {
            alert(`Payment method not selected`, this.appInfo.settings.church.toUpperCase());
            return;
        }
        const result = await confirm(`Are you sure you want to mark this invoice as paid?`, this.appInfo.settings.church.toUpperCase());
        if (!result) return;

        this.appInfo.pleaseWaitShow();

        this.invoice.paidAmount = this.invoice.total;
        this.invoice.paid = new Date();
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
    }

    async copyBilling() {
        if (!this.invoice) return;

        // const customer = this.invoice.customer;
        // this.invoice.billToContactName = customer?.contactName;
        // this.invoice.billToAddress1 = customer?.billAddress1;
        // this.invoice.billToAddress2 = customer?.billAddress2;
        // this.invoice.billToCity = customer?.billCity;
        // this.invoice.billToState = customer?.billState;
        // this.invoice.billToZip = customer?.billZip;
        // this.invoice.billToContactName = customer?.contactName;
        // this.invoice.billToCountry = customer?.billCountry;
        // this.invoice.billToEmail = customer?.email;
        // this.invoice.billToPhone = customer?.phone;
    }

    calcTotals() {
        // this.calcSubTotal();
        // this.calcTax();

        if (this.invoice.total !== this.invoice.totalCalc) this.invoice.total = this.invoice.totalCalc;
    }

    calcSubTotal() {
        let subTotal = 0;
        let taxableSubTotal = 0;
    }

    calcTax() {
        let tax = 0;
    }

    async addRoundUp() {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Round Up',
                dialogPrompt: 'Enter amount to round up to:',
                minValue: 0,
                checkNumeric: true,
                mode: 'text',
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                if (result.value <= this.invoice.totalCalc) {
                    alert(`Amount must be greater than the current total of $${this.invoice.totalCalc.toFixed(2)}`, this.appInfo.settings.church.toUpperCase());
                    return;
                }

                const line = this.uow.invoiceLines.createEntity({
                    invoiceId: this.invoice.id,
                    lineNumber: this.nextLineNumber(),
                    partNumber: 'Round Up Donation',
                    description: 'Round Up Donation',
                    quantity: 1,
                    price: (result.value-this.invoice.totalCalc).toFixed(2),
                    isDonationQualified: true,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as InvoiceLine;
                this.invoiceLines.push(line);
                this.calcTotals();
                await this.uow.commit();
                await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    async openLine(invoiceLine?: InvoiceLine, quantity?: number) {
        const modalRef = this.modalService.show(InvoiceLineDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                invoiceId: this.invoice.id,
                lineNumber: this.nextLineNumber(),
                invoiceLine: invoiceLine,
                quantity: quantity ? quantity : 1,
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                if (!this.invoiceLines.includes(result.value)) this.invoiceLines.push(result.value);
                await this.uow.commit([result.value]);
                await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    async removeLine(invoiceLines?: InvoiceLine[]) {
        const result = await confirm(`Are you sure you want to remove the selected invoice line?`, this.appInfo.settings.church.toUpperCase());
        if (!result) return;

        const save = await this.save();
        if (!save) return;

        for (let index = 0; index < invoiceLines.length; index++) {
            const line = invoiceLines[index];
            line.auctionPurchaseId = null;
            this.invoiceLines.splice(this.invoiceLines.indexOf(line), 1);
            line.deleted = new Date();
            line.deletedBy = this.auth.currentUser.userName;
        }

        this.updateDisplayOrder();
        await this.uow.commit();
        this.calcTotals();
        await this.getData();
        await this.uow.commit();
    }

    toggleIsDonation(line: InvoiceLine) {
        if (this.readOnly || !line) return;
        line.isDonationQualified = !line.isDonationQualified;
        this.calcTotals();
    }

    async save() {
        const success = await Promise.resolve(this.validate());
        if (!success) return false;
        this.appInfo.pleaseWaitShow();
        this.isToastVisible = true;
        console.log('saveCalc');
        this.calcTotals();

        if (!this.invoice.invoiceNumber) this.invoice.invoiceNumber = (await this.utility.getNextNumber('InvoiceNumber')) as any;

        await this.uow.commit();
        if (this.invoiceId === 'new') {
            this.invoiceId = this.invoice.id;
            this.router.navigate(['/invoices/' + this.invoice.id], { replaceUrl: true });
        }
        await this.getData();
        this.appInfo.pleaseWaitHide();
        return true;
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }
    closeDialog() {
        if (this.showAsDialog) {
            this.uow.rollback();
            this.result.next({});
        }
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    async email() {
            if (!this.invoice.email) {
                alert('Email address is blank. Please provide an email address.', this.appInfo.settings.church.toUpperCase());
                return;
            }
        
        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'Invoice',
                entity: this.invoice,
                to: this.invoice.email,
                hideAttachments: true,
                hideCommittee: true,
                subject: `Invoice #${this.invoice.invoiceNumber}`,
                body: `Please find attached invoice #${this.invoice.invoiceNumber} for your review.\r\n\r\nPlease do not respond to this email. If you have any questions, please contact the church office.`,
                // entityAttachments: this.attachments,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && result.value) {
                // email.committeeId = result.value;
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.utility.emailInvoice(result.value.id, this.invoice.id);
                this.appInfo.pleaseWaitHide();
                const status = alert(`Email Sent!`, `${this.appInfo.settings.church.toUpperCase()}`);
            }
            modalRef.hide();
        });
    }

    async print() {
        await this.utility.printInvoice(this.invoice.id);
    }

    async move(invoiceLine: InvoiceLine, direction: 'up' | 'down') {
        if (this.processing) return;
        this.processing = true;
        const increment = direction === 'down' ? 1 : -1;
        const otherInvoiceLine = this.invoiceLines.find((otherInvoiceLine) => otherInvoiceLine.lineNumber === invoiceLine.lineNumber + increment);
        if (otherInvoiceLine) {
            invoiceLine.lineNumber += increment;
            otherInvoiceLine.lineNumber -= increment;
            this.sort(this.invoiceLines);
            await this.save();
        }
        this.processing = false;
    }

    sort(invoiceLines: InvoiceLine[]) {
        invoiceLines.sort((a, b) => (a.lineNumber || 9999) - (b.lineNumber || 9999));
    }

    updateDisplayOrder() {
        _.orderBy(this.invoiceLines, 'lineNumber').forEach((invoiceLine, index) => {
            if (invoiceLine.lineNumber !== index + 1) invoiceLine.lineNumber = index + 1;
        });
    }

    nextLineNumber() {
        return (_(this.invoiceLines).maxBy((l) => l.lineNumber)?.lineNumber ?? 0) + 1;
    }
}
